// Deterministic particle maths for the FX emitter: a seeded generator fixes
// each particle's traits once, and positions are pure functions of elapsed
// time, so playback is identical for a given seed (replay and test friendly).

export type FxEmitterPreset = 'spark_burst' | 'smoke_soft' | 'ember_drift' | 'pickup_glow';

export const FX_EMITTER_PRESETS: readonly FxEmitterPreset[] = [
  'spark_burst',
  'smoke_soft',
  'ember_drift',
  'pickup_glow',
];

/** Per-particle traits fixed at seeding time. */
export interface ParticleSeed {
  /** 0..1 cycle offset so particles do not move in lockstep. */
  phase: number;
  /** Emission direction on the ground plane, radians. */
  angle: number;
  /** Lateral reach of the particle's path. */
  radius: number;
  /** Vertical speed factor. */
  rise: number;
  /** Sway frequency factor. */
  sway: number;
}

/** Mulberry32: tiny deterministic PRNG, identical sequence per seed. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Hash an instance id (or any string) to a numeric emitter seed. */
export function hashSeed(text: string): number {
  let hash = 2166136261;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

/** Fix `count` particles' traits from the seed; pure and reproducible. */
export function buildParticleSeeds(seed: number, count: number): ParticleSeed[] {
  const rand = mulberry32(seed);
  return Array.from({ length: count }, () => ({
    phase: rand(),
    angle: rand() * Math.PI * 2,
    radius: 0.2 + rand() * 0.8,
    rise: 0.5 + rand() * 0.9,
    sway: 0.5 + rand() * 1.5,
  }));
}

const CYCLE_SECONDS = 2.4;

/**
 * A particle's local position at elapsed time `t`, pure per (preset, seed).
 * All presets cycle every CYCLE_SECONDS scaled by the particle's traits.
 */
export function particlePosition(
  preset: FxEmitterPreset,
  seed: ParticleSeed,
  t: number,
): [number, number, number] {
  // 0..1 through this particle's current cycle.
  const cycle = (t / CYCLE_SECONDS + seed.phase) % 1;
  switch (preset) {
    case 'spark_burst': {
      // Shoot outward and up, falling back as the cycle ends.
      const reach = cycle * seed.radius * 1.6;
      const y = cycle * seed.rise * 1.4 - cycle * cycle * 1.6;
      return [Math.cos(seed.angle) * reach, Math.max(0, y) + 0.05, Math.sin(seed.angle) * reach];
    }
    case 'smoke_soft': {
      // Slow rise, widening drift.
      const y = cycle * seed.rise * 2.2;
      const drift = 0.15 + cycle * seed.radius * 0.7;
      const wobble = Math.sin((cycle * 4 + seed.phase * 6.28) * seed.sway);
      return [Math.cos(seed.angle) * drift * wobble, y, Math.sin(seed.angle) * drift * wobble];
    }
    case 'ember_drift': {
      // Steady upward drift with sideways sway.
      const y = cycle * seed.rise * 1.8;
      const sway = Math.sin(t * seed.sway + seed.phase * 6.28) * seed.radius * 0.4;
      return [Math.cos(seed.angle) * 0.3 + sway, y, Math.sin(seed.angle) * 0.3 + sway * 0.6];
    }
    case 'pickup_glow': {
      // Orbit a slow bobbing ring.
      const orbit = seed.angle + t * (0.8 + seed.sway * 0.4);
      const y = 0.5 + Math.sin(t * 1.4 + seed.phase * 6.28) * 0.25 + seed.phase * 0.3;
      return [Math.cos(orbit) * seed.radius * 0.6, y, Math.sin(orbit) * seed.radius * 0.6];
    }
  }
}
